<template>
  <div>
    <div class="form-group">
      <label for="location_name">Name <span class="text-danger">*</span></label>
      <input
        v-model="name"
        type="text"
        id="location_name"
        class="form-control"
        placeholder="e.g., Oak Park"
        maxlength="255"
      />
      <p class="help-block text-gray-600">A short name your team recognizes, like the neighborhood or facility name.</p>
    </div>

    <div class="form-group">
      <label for="location_time_zone">Time zone</label>
      <select v-model="timeZone" id="location_time_zone" class="form-control" data-cy="location-time-zone">
        <option value="">{{ teamZoneLabel }} (team default)</option>
        <option v-for="option in timeZoneOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
      </select>
      <p class="help-block text-gray-600">Only change this if the location is in a different time zone than your team. Events here are then scheduled, shown, and sent in notifications on this location's local time.</p>
    </div>

    <h3 class="text-18 font-semi m-t-md m-b-sm">Address <span class="text-gray-600 font-normal text-14">(optional)</span></h3>

    <div class="form-group">
      <label for="location_address_line1">Address line 1</label>
      <input v-model="addressLine1" type="text" id="location_address_line1" class="form-control" maxlength="255" />
    </div>

    <div class="form-group">
      <label for="location_address_line2">Address line 2</label>
      <input v-model="addressLine2" type="text" id="location_address_line2" class="form-control" maxlength="255" />
    </div>

    <div class="row">
      <div class="col-xs-12 col-md-6">
        <div class="form-group">
          <label for="location_city">City</label>
          <input v-model="city" type="text" id="location_city" class="form-control" maxlength="255" />
        </div>
      </div>
      <div class="col-xs-6 col-md-3">
        <div class="form-group">
          <label for="location_state">State</label>
          <input v-model="state" type="text" id="location_state" class="form-control" maxlength="255" />
        </div>
      </div>
      <div class="col-xs-6 col-md-3">
        <div class="form-group">
          <label for="location_postal_code">Postal code</label>
          <input v-model="postalCode" type="text" id="location_postal_code" class="form-control" maxlength="20" />
        </div>
      </div>
    </div>

    <div v-if="editing" class="form-group">
      <div class="checkbox">
        <label>
          <input v-model="archived" type="checkbox" />
          Archived
        </label>
      </div>
      <p class="help-block text-gray-600">Archived locations are hidden from filters but stay linked to existing rosters and sessions.</p>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="alert alert-danger m-t-md">
      {{ errorMessage }}
    </div>

    <!-- Save/Cancel Footer -->
    <div class="flex gap-sm m-t-lg p-t-md border-t border-solid border-gray-200">
      <ladda-button @lbClicked="save" el-class="btn-primary" :loading="saving">
        {{ editing ? 'Update Location' : 'Create Location' }}
      </ladda-button>
      <a :href="backUrl" class="btn btn-default">Cancel</a>
    </div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { errorableMix } from './mixins/errorable_mix';
import {
  sameClock, teamTimeZone, zoneLabel, TIME_ZONE_OPTIONS,
} from '../event_time_zone';

export default {
  name: 'location-form',
  components: { LaddaButton },
  mixins: [errorableMix],
  props: {
    existingLocation: {
      type: Object,
      default: null,
    },
    backUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      // '' = follow the team's zone (sent as blank, stored as null)
      timeZone: '',
      archived: false,
      saving: false,
    };
  },
  computed: {
    editing() {
      return !!this.existingLocation;
    },
    teamZoneLabel() {
      const team = teamTimeZone();
      return team ? zoneLabel(team) : 'Same as the rest of the team';
    },
    // The team-default row already covers the team's clock, so drop entries
    // that keep the same time all year. A zone set outside the curated list
    // (e.g. by support) stays selectable so saving never silently clears it.
    timeZoneOptions() {
      const team = teamTimeZone();
      const current = this.existingLocation && this.existingLocation.time_zone;
      const options = TIME_ZONE_OPTIONS.filter((o) => o.value === current || !team || !sameClock(o.value, team));
      if (current && !TIME_ZONE_OPTIONS.some((o) => o.value === current)) {
        return [...options, { value: current, label: zoneLabel(current) }];
      }
      return options;
    },
  },
  mounted() {
    if (this.existingLocation) {
      this.name = this.existingLocation.name;
      this.addressLine1 = this.existingLocation.address_line1 || '';
      this.addressLine2 = this.existingLocation.address_line2 || '';
      this.city = this.existingLocation.city || '';
      this.state = this.existingLocation.state || '';
      this.postalCode = this.existingLocation.postal_code || '';
      this.timeZone = this.existingLocation.time_zone || '';
      this.archived = this.existingLocation.archived;
    }
  },
  methods: {
    buildParams() {
      return {
        location: {
          name: this.name,
          address_line1: this.addressLine1,
          address_line2: this.addressLine2,
          city: this.city,
          state: this.state,
          postal_code: this.postalCode,
          time_zone: this.timeZone,
          archived: this.archived,
        },
      };
    },
    async save() {
      this.saving = true;
      this.clearError();

      if (!this.name.trim()) {
        this.showError('Error: name is required.');
        this.saving = false;
        return;
      }

      const url = this.editing
        ? this.$apiService.locationUrl(this.existingLocation.id)
        : this.$apiService.locationsUrl();

      const method = this.editing ? 'put' : 'post';

      try {
        await axios[method](url, this.buildParams());
        window.location.assign(this.backUrl);
      } catch (error) {
        this.showError(this.parseErrorResponse(error));
        window.scrollTo(0, 0);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
